import type {
  DuplicateCheckConfig,
  DuplicateCheckFieldValue,
  DuplicateCheckResultComposite,
  DuplicateCheckResultSingle,
  MappingJSON,
} from '../types';

export interface FieldAttributeMaps {
  fieldToAttribute: Record<string, string>;
  attributeToField: Record<string, string>;
}

/**
 * Builds bidirectional maps between form field names (sourcePath)
 * and backend attribute names (targetPath) from the mapping array.
 */
export function buildFieldAttributeMaps(
  mapping: MappingJSON[]
): FieldAttributeMaps {
  const fieldToAttribute: Record<string, string> = {};
  const attributeToField: Record<string, string> = {};

  mapping.forEach((m) => {
    if (m.sourcePath && m.targetPath) {
      fieldToAttribute[m.sourcePath] = m.targetPath;
      attributeToField[m.targetPath] = m.sourcePath;
    }
  });

  return { fieldToAttribute, attributeToField };
}

/**
 * Extracts all attribute names from the duplicate check config (single + composite),
 * reverse-maps them to form field names, and returns the set of field names
 * that participate in duplicate checking.
 */
export function getDuplicateCheckFieldNames(
  config: DuplicateCheckConfig,
  maps: FieldAttributeMaps
): Set<string> {
  const fieldNames = new Set<string>();

  config.single?.forEach((rule) => {
    const fieldName = maps.attributeToField[rule.field];

    if (fieldName) {
      fieldNames.add(fieldName);
    }
  });

  config.composite?.forEach((rule) => {
    rule.fields?.forEach((attr) => {
      const fieldName = maps.attributeToField[attr];

      if (fieldName) {
        fieldNames.add(fieldName);
      }
    });
  });

  return fieldNames;
}

/**
 * Takes Formik values for watched fields and converts them to
 * the API request format using attribute names.
 */
export function mapFieldValuesToAttributes(
  fieldValues: Record<string, unknown>,
  watchedFields: Set<string>,
  maps: FieldAttributeMaps
): DuplicateCheckFieldValue[] {
  const result: DuplicateCheckFieldValue[] = [];

  watchedFields.forEach((fieldName) => {
    const value = fieldValues[fieldName];
    const attributeName = maps.fieldToAttribute[fieldName];

    if (
      attributeName &&
      (typeof value === 'string' || typeof value === 'number') &&
      value
    ) {
      result.push({ fieldName: attributeName, fieldValue: value });
    }
  });

  return result;
}

/**
 * Takes API response results, reverse-maps attribute names back to form field
 * names, and returns a Record<formFieldName, errorMessage> for single-field
 * duplicate errors only. Composite duplicates are excluded from this mapping
 * since they are displayed as a banner.
 */
export function mapAttributeResultsToFieldErrors(
  results: (DuplicateCheckResultSingle | DuplicateCheckResultComposite)[],
  maps: FieldAttributeMaps,
  errorMessage: string
): Record<string, string> {
  const errors: Record<string, string> = {};

  results.forEach((result) => {
    if (!result.exists) {
      return;
    }

    if ('fieldName' in result) {
      const fieldName = maps.attributeToField[result.fieldName];

      if (fieldName) {
        errors[fieldName] = errorMessage;
      }
    }
  });

  return errors;
}

/**
 * Checks whether a result is a composite duplicate result.
 */
export function isCompositeResult(
  result: DuplicateCheckResultSingle | DuplicateCheckResultComposite
): result is DuplicateCheckResultComposite {
  return 'compositeFields' in result;
}

/**
 * Extracts composite duplicate messages from API results.
 * Returns the composite error message string if a composite duplicate exists,
 * or null otherwise. Uses field labels (when provided) for user-friendly display.
 */
export function getCompositeDuplicateMessage(
  results: (DuplicateCheckResultSingle | DuplicateCheckResultComposite)[],
  maps: FieldAttributeMaps,
  messageTemplate: string,
  fieldNameToLabel?: Record<string, string>
): string | null {
  const compositeResult = results.find(
    (r) => isCompositeResult(r) && r.exists
  ) as DuplicateCheckResultComposite | undefined;

  if (!compositeResult) {
    return null;
  }

  const fieldLabels = compositeResult.compositeFields
    .map((cf) => {
      const fieldName = maps.attributeToField[cf.fieldName];

      return fieldNameToLabel?.[fieldName] ?? fieldName ?? cf.fieldName;
    })
    .join(' + ');

  return messageTemplate.replace('{fields}', fieldLabels);
}
